// @flow
import { useState } from 'react';
import styled from '@emotion/styled';
import { SPACING } from '@tablekit/theme';
import type {
  PagePropsType,
  BlokComponentType,
  DocType,
  IconWithTitleType,
  SeoType,
  CategoryType,
  AuthorType
} from 'components/types';
import {
  getPageContext,
  handleEditorValues,
  EDITOR_VALUES
} from 'components/PageContext';
import { CallToActionBackground } from 'components/CallToAction';
import { MOBILE_BREAKPOINT } from 'utils/theme';
import AuthorFooter from './AuthorFooter';
import Content from './Content';
import HeroBanner from './HeroBanner';
import PostHeader from './PostHeader';
import RelatedPosts from './RelatedPosts';
import SiblingLinks from './SiblingLinks';
import ShareButtons from './ShareButtons';
import Summary from './Summary';
import { PostBody, SummaryButton } from './styled';
import type { BlogBodyType, SiblingLinkType, BlogRelatedType } from './types';

type BlogPostPropsType = {
  author: AuthorType,
  body: BlogBodyType,
  categories: CategoryType[],
  hero_image: string,
  hero_quick_icons: BlokComponentType<IconWithTitleType>[],
  hero_subtitle?: string,
  hero_title: string,
  hero_user_text?: string,
  next?: SiblingLinkType,
  prev?: SiblingLinkType,
  read_time?: number,
  related: BlogRelatedType,
  seo: SeoType,
  show_summary: boolean,
  summary_text: DocType
};

const MobileOnlySummaryButtonWrapper = styled.div`
  display: none;
  @media ${MOBILE_BREAKPOINT} {
    display: flex;
    justify-content: flex-end;
    margin-top: ${SPACING.L2};
  }
`;
const DesktopOnlySummaryButtonWrapper = styled.div`
  display: block;
  @media ${MOBILE_BREAKPOINT} {
    display: none;
  }
`;

const BlogPost = ({
  blok: {
    body,
    author: blockAuthor,
    categories,
    hero_image: heroImage,
    hero_quick_icons: heroIcons,
    hero_subtitle: heroSubtitle,
    hero_title: heroTitle,
    hero_user_text: heroUserText,
    next,
    prev,
    read_time: readTime,
    related,
    // eslint-disable-next-line no-unused-vars
    seo,
    show_summary: showSummary,
    summary_text: summaryText
  },
  id
}: PagePropsType<BlogPostPropsType>) => {
  // Note that related will be undefined when editing in StoryBlok due to dynamically loading data
  const hasRelated = related && related.posts && related.posts.length > 0;
  let author = blockAuthor;
  if (CONFIG.storyblok.version === 'draft') {
    author = handleEditorValues(EDITOR_VALUES.PROFILE, blockAuthor);
  }
  const { blogConfig } = getPageContext();
  let isSummaryShown = false;
  // eslint-disable-next-line no-unused-vars
  let setIsSummaryShown = (isShown: boolean) => {};
  let desktopSummaryButton = null;
  let mobileSummaryButton = null;
  if (showSummary && summaryText) {
    [isSummaryShown, setIsSummaryShown] = useState(
      blogConfig.show_summary_open
    );
    const summaryButton = (
      <SummaryButton
        appearance="subtle"
        type="button"
        onClick={() => setIsSummaryShown(!isSummaryShown)}
        isActive={isSummaryShown}
        size={{ [MOBILE_BREAKPOINT]: 'small', default: 'regular' }}
      >
        {isSummaryShown
          ? blogConfig.hide_summary_text
          : blogConfig.show_summary_text}
      </SummaryButton>
    );
    desktopSummaryButton = (
      <DesktopOnlySummaryButtonWrapper>
        {summaryButton}
      </DesktopOnlySummaryButtonWrapper>
    );
    mobileSummaryButton = (
      <MobileOnlySummaryButtonWrapper>
        {summaryButton}
      </MobileOnlySummaryButtonWrapper>
    );
  }

  return (
    <div id={id}>
      <HeroBanner
        image={heroImage}
        categories={categories}
        title={heroTitle}
        subtitle={heroSubtitle}
        userText={heroUserText}
        icons={heroIcons}
      />
      <PostBody>
        {mobileSummaryButton}
        {showSummary && (
          <Summary isMobileOnly text={summaryText} isOpen={isSummaryShown} />
        )}
        <PostHeader
          author={author}
          readTime={readTime}
          summaryButton={desktopSummaryButton}
        />
        {showSummary && (
          <Summary text={summaryText} isOpen={isSummaryShown} isDesktopOnly />
        )}
        <Content content={body} />
        <ShareButtons />
        <AuthorFooter author={author} />
        <SiblingLinks next={next} prev={prev} />
        {!hasRelated && <CallToActionBackground />}
      </PostBody>
      {hasRelated && <RelatedPosts config={related} blogConfig={blogConfig} />}
    </div>
  );
};

export default BlogPost;
